import { CardComponentProps } from "@~/types/types";
import styles from "./CallToAction.module.scss";
import {
  PrimaryButton,
  SecondaryButton,
} from "@JohnLewisPartnership/jl-design-system/dist/elements/button/Button";

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    jlData: any;
  }
}

import {
  getProductUrl,
  isAddToBasketProduct,
  isEmailWhenBackInStockProduct,
  isInStoreOnlyProduct,
  isMultiSkuProduct,
  isPermanentlyOutOfStockProduct,
  isPreOrderWithoutNotifiableEventsProduct,
  isProductInCachedBasket,
  isProductSetProduct,
  isShipFromStoreProduct,
  isViewOptionsProduct,
} from "../../utils/product";
import { useEmailConfirmed } from "@~/product-card-components/__shared__/AddToBasketUtils/hooks/useEmailConfirmed";
import { useCachedBasketData } from "@~/product-card-components/__shared__/AddToBasketUtils/hooks/useCachedBasket";
import { AddToBasketButton } from "./AddToBasketButton/AddToBasketButton";
import { QuickViewModal } from "../__shared__/QuickViewModal/QuickViewModal";
import { useRef, useState } from "react";
import { useWindowSize } from "../../utils/hooks/useWindowSize";
import { useVariant } from "@~/contexts/VariantContext/VariantContext";

export function CallToAction({
  product,
  configProps,
}: Pick<CardComponentProps, "product" | "configProps">) {
  const buttonContent = CallToActionButton({ product, configProps });

  if (buttonContent === null) return null;

  return <div data-component="CallToAction">{buttonContent}</div>;
}

export function CallToActionButton({
  product,
  configProps,
}: Pick<CardComponentProps, "product" | "configProps">) {
  const [showQVModal, setShowQVModal] = useState(false);
  const { variant } = useVariant();
  const ref = useRef(null);

  const { width: windowWidth } = useWindowSize();
  const isSmallScreen = windowWidth ? windowWidth < 768 : false;

  const { pdpURL: { url } = {}, title } = product;
  const isMultiSku = isMultiSkuProduct(product);
  const isProductSet = isProductSetProduct(product);
  const clientId = configProps?.clientId as string;

  const showQuickView = () => {
    if (typeof window?.performance?.mark === "function") {
      performance.mark("quickViewDisplayed-start");
    }

    setShowQVModal(true);
  };

  const hasAddToBasketButton = !configProps?.hideAddToBasket;
  const hasGoToBasketButton = !configProps?.hideGoToBasket;
  const hasEmailWhenAvailableButton = !configProps?.hideEmailWhenAvailable;
  const hasInStoreOnlyButton = !configProps?.hideInStoreOnly;

  const hasMoreInformationButton =
    !configProps?.hideAddToBasket || isProductSet;
  const shouldUsePrimaryButton = configProps?.usePrimaryButton ?? false;

  const hasOutOfStockButton = !configProps?.hideOutOfStock;
  const hasViewOptionsButton = configProps?.showViewOptions ?? false;
  const handleMoreInformationClick = configProps?.handleMoreInformationClick as
    | ((event: React.MouseEvent<Element>) => void)
    | undefined;

  const shouldShowAddToBasketButton = isAddToBasketProduct(product);
  const shouldShowEmailWhenAvailable = isEmailWhenBackInStockProduct(product);
  const shouldShowInStoreOnly = isInStoreOnlyProduct(product);
  const shouldShowPermanentlyOutOfStock =
    isPermanentlyOutOfStockProduct(product);
  const shouldShowViewOptions = isViewOptionsProduct(product);

  const hasHadEmailConfirmed = useEmailConfirmed(
    shouldShowEmailWhenAvailable,
    product?.aliases?.pimProductId,
  );

  const cachedBasketData = useCachedBasketData();

  if (hasViewOptionsButton && shouldShowViewOptions) {
    return (
      <SecondaryButton
        className={styles.cta_button}
        href={url}
        size="small"
        tag="a"
      >
        View options
        <span className={styles.visually_hidden}>{`, ${title}`}</span>
      </SecondaryButton>
    );
  }

  if (hasInStoreOnlyButton && shouldShowInStoreOnly) {
    return (
      <PrimaryButton
        className={styles.cta_button}
        href={url}
        size="small"
        tag="a"
      >
        In-store only
      </PrimaryButton>
    );
  }

  if (hasOutOfStockButton && shouldShowPermanentlyOutOfStock) {
    return (
      <PrimaryButton
        className={styles.cta_button}
        href={url}
        size="small"
        tag="a"
        data-testid="Out Of Stock Button"
        disabled
      >
        Out of stock
      </PrimaryButton>
    );
  }

  if (hasEmailWhenAvailableButton && shouldShowEmailWhenAvailable) {
    if (!hasHadEmailConfirmed || showQVModal) {
      return (
        <>
          <div className="CallToActionPortalContainer" ref={ref} />
          <SecondaryButton
            className={styles.cta_button}
            size="small"
            disabled={showQVModal}
            submitting={showQVModal}
            onClick={showQuickView}
            data-testid="component-email-bis-button"
          >
            Email when available
            <span className={styles.visually_hidden}>{`, ${title}`}</span>
          </SecondaryButton>
          <QuickViewModal
            product={product}
            showModal={showQVModal}
            onClose={() => setShowQVModal(false)}
            config={isSmallScreen ? "quickAdd" : "standard"}
            originator={"ADD_TO_BASKET"}
            portalContainer={ref.current || undefined}
          ></QuickViewModal>
        </>
      );
    }

    if (hasHadEmailConfirmed && !showQVModal) {
      return (
        <SecondaryButton
          className={styles.cta_button}
          href={url}
          size="small"
          tag="a"
        >
          We'll be in touch
        </SecondaryButton>
      );
    }
  }

  if (hasAddToBasketButton && shouldShowAddToBasketButton) {
    const atbButtonText = `to ${!configProps?.shortATBText ? "your" : ""} basket`;

    const isProductInBasket = isProductInCachedBasket(
      product.aliases?.pimProductId,
      cachedBasketData,
    );
    const isBasketPageType =
      typeof window !== "undefined"
        ? window.jlData?.page?.pageInfo?.pageType === "shopping basket"
        : false;

    if (hasGoToBasketButton && isProductInBasket && !isBasketPageType) {
      return (
        <SecondaryButton
          className={styles.cta_button}
          aria-label={`${title} is in your basket. Go to basket`}
          href="/basket"
          size="small"
          tag="a"
        >
          Go {atbButtonText}
        </SecondaryButton>
      );
    }

    if (!isProductInBasket || isBasketPageType) {
      return (
        <>
          <div className="CallToActionPortalContainer" ref={ref} />
          <AddToBasketButton
            className={styles.cta_button}
            product={product}
            longCtaText={configProps?.longCtaText === true}
            clientId={clientId}
            handleMultiSku={isMultiSku ? showQuickView : undefined}
          />
          <QuickViewModal
            product={product}
            showModal={showQVModal}
            onClose={() => setShowQVModal(false)}
            modalTitle={isSmallScreen ? "Confirm colour and size" : ""}
            config={isSmallScreen ? "quickAdd" : "standard"}
            originator={"ADD_TO_BASKET"}
            portalContainer={ref.current || undefined}
          ></QuickViewModal>
        </>
      );
    }
  }

  if (
    hasMoreInformationButton ||
    isPreOrderWithoutNotifiableEventsProduct(product)
  ) {
    const moreInformationLabel = configProps?.moreInformationLabel
      ? (configProps.moreInformationLabel as string)
      : isProductSet
        ? "View range"
        : "More information";

    if (shouldUsePrimaryButton) {
      return (
        <PrimaryButton
          data-testid={"call-to-action-more-information-primary"}
          className={styles.cta_button}
          href={getProductUrl(product, variant)}
          size="small"
          tag="a"
        >
          {moreInformationLabel}
        </PrimaryButton>
      );
    } else {
      const isShipFromStore = isShipFromStoreProduct(product);
      return (
        <SecondaryButton
          className={styles.cta_button}
          data-test-ship-from-store={isShipFromStore}
          onClick={handleMoreInformationClick}
          href={getProductUrl(product, variant)}
          size="small"
          tag="a"
        >
          {moreInformationLabel}
        </SecondaryButton>
      );
    }
  }

  return null;
}
